import {
  dedupeIntegration,
  extraErrorDataIntegration,
  init,
  replayIntegration,
} from '@sentry/react';
import type { Event, Exception } from '@sentry/types';

import Logger from '@rover/rsdk/src/modules/Logging/Logger';
import sentryBrowserFilterOptions from '@rover/utilities/sentryBrowserFilterOptions';

// Don't destructure because webpack.EnvironmentPlugin can't replace destructured vars
const SENTRY_DSN = process.env.SENTRY_DSN; // eslint-disable-line prefer-destructuring
const ROVER_ENVIRONMENT = process.env.ROVER_ENVIRONMENT || 'development';
const ENVIRONMENT = `${ROVER_ENVIRONMENT}-client`;

if (!SENTRY_DSN || !SENTRY_DSN.length) {
  console.error('SENTRY_DSN environment variable was not found'); // eslint-disable-line no-console
}

const HYDRATION_ERROR_SAMPLE_RATE = 0.01;

const HYDRATION_ERROR_REGEX =
  /Hydration failed|Text content does not match|Expected server HTML to contain|There was an error while hydrating|server rendered.*didn't match.*client|hydration.*mismatch/i;

function isHydrationText(text: string): boolean {
  return HYDRATION_ERROR_REGEX.test(text);
}

function isHydrationErrorInEvent(event: Event): boolean {
  // Check exceptions
  if (
    event.exception?.values?.some(
      (exception: Exception) =>
        isHydrationText(exception.value || '') || exception.type === 'ChunkLoadError'
    )
  ) {
    return true;
  }

  // Check event message
  if (event.message && isHydrationText(event.message)) {
    return true;
  }

  // Check breadcrumbs
  if (
    event.breadcrumbs?.some((breadcrumb) =>
      isHydrationText(`${breadcrumb.message || ''} ${JSON.stringify(breadcrumb.data || {})}`)
    )
  ) {
    return true;
  }

  // Check tags
  if (
    event.tags &&
    Object.values(event.tags).some((tag) => typeof tag === 'string' && /hydration/i.test(tag))
  ) {
    return true;
  }

  // Check fingerprint
  if (event.fingerprint?.some((fp) => typeof fp === 'string' && /hydration/i.test(fp))) {
    return true;
  }

  return false;
}

init({
  dsn: SENTRY_DSN,
  environment: ENVIRONMENT,
  release: process.env.RELEASE_ID || 'unknown',
  integrations: [dedupeIntegration(), extraErrorDataIntegration(), replayIntegration()],
  beforeSend(event) {
    // Check if this event contains a hydration error (in any part of the event)
    const hasHydrationError = isHydrationErrorInEvent(event);

    if (hasHydrationError) {
      Logger.debug('Sentry: Hydration error detected', {
        eventId: event.event_id || 'unknown',
        message: event.message || 'no-message',
        hasExceptions: String(!!event.exception?.values?.length),
        hasBreadcrumbs: String(!!event.breadcrumbs?.length),
        sampleRate: String(HYDRATION_ERROR_SAMPLE_RATE),
      });

      // Only report 1% of hydration errors (1 out of 100)
      if (Math.random() > HYDRATION_ERROR_SAMPLE_RATE) {
        Logger.debug('Sentry: Hydration error filtered (not sampled)');
        return null;
      }

      Logger.debug('Sentry: Hydration error sampled and will be sent');
      return {
        ...event,
        tags: { ...event.tags, hydration_error_sampled: true },
      };
    }

    return event; // Send all other errors normally
  },
  initialScope: {
    tags: {
      node_env: process.env.NODE_ENV,
    },
  },
  replaysSessionSampleRate: 0.001,
  replaysOnErrorSampleRate: 0.001,
  ...sentryBrowserFilterOptions,
});
